import React, { useContext } from 'react';
import { arrayOf, shape, string, func, number } from 'prop-types';
import classNames from 'classnames';
import { Action, Label } from '../../prop-types';
import OtherSellersItem from './other-sellers-item';
import { componentEnhance } from '@vpp-frontend-components/component-enhance';
import { trackEvent } from '../../lib/tracking';
import StaticPropsContext from '../context/static-props';
import { PriceFontSize } from '../../lib/num-font-size-enum';
import useFetchDeferred from '../../hooks/use-fetch-deferred';
import { CardTooltipSkeleton } from '../placeholder';
import SeoAnchor from '@seo-frontend-components/seo-anchor';

const namespace = 'ui-pdp-other-sellers';

const OtherSellers = ({
  action,
  className,
  items,
  parentUrl,
  selectedQuantity,
  title,
  showAddToCartModal,
  runCatchErrorBoundary,
  state,
  onFetchDeferredComponent,
  id,
}) => {
  try {
    /* eslint-disable react-hooks/rules-of-hooks */
    const { lowEnd, deviceType } = useContext(StaticPropsContext);
    const collapsable = deviceType === 'mobile' && !lowEnd;
    const { label, target, track } = action;
    const isDeferred = state === 'DEFERRED';
    const refView = isDeferred ? useFetchDeferred(onFetchDeferredComponent, id) : null;
    const customClass = {
      'ui-pdp-other-sellers--loading': isDeferred,
    };
    const SKELETON_HEIGHT = 150;

    return (
      <div ref={refView} className={classNames(namespace, className, customClass)}>
        {isDeferred ? (
          <CardTooltipSkeleton height={SKELETON_HEIGHT} />
        ) : (
          <>
            <h2 className={`${namespace}__title`} data-testid="title">
              {componentEnhance.jsx(
                title.template_text || title.text,
                title.values,
                null,
                PriceFontSize.OTHER_SELLERS.ORIGINAL,
              )}
            </h2>
            <ul data-testid="items">
              {items.map(item => (
                <OtherSellersItem
                  key={`other-seller-item-${item.id}`}
                  {...item}
                  collapsable={collapsable}
                  parentUrl={parentUrl}
                  selectedQuantity={selectedQuantity}
                  showAddToCartModal={showAddToCartModal}
                />
              ))}
            </ul>
            <SeoAnchor
              className={`${namespace}__button`}
              href={target}
              onClick={() => {
                trackEvent(track);
              }}
            >
              {componentEnhance.jsx(label.text, label.values, null, PriceFontSize.OTHER_SELLERS.LINK)}
            </SeoAnchor>
          </>
        )}
      </div>
    );
    /* eslint-enable react-hooks/rules-of-hooks */
  } catch (error) {
    /* istanbul ignore next */
    return runCatchErrorBoundary(error);
  }
};

OtherSellers.propTypes = {
  action: Action,
  className: string,
  items: arrayOf(shape(OtherSellersItem.propsTypes)).isRequired,
  parentUrl: string.isRequired,
  selectedQuantity: number.isRequired,
  title: Label.isRequired,
  showAddToCartModal: func,
  state: string.isRequired,
  onFetchDeferredComponent: func,
  runCatchErrorBoundary: func,
};

OtherSellers.defaultProps = {
  action: {
    label: null,
    target: null,
    track: null,
  },
  className: null,
  showAddToCartModal: null,
  onFetchDeferredComponent: null,
  runCatchErrorBoundary: () => {},
};

export default OtherSellers;
